import { Link } from "react-router-dom";
import { ReactElement, useEffect, useState } from "react";
import { BiLogoYoutube } from "react-icons/bi";
import { FaReact } from "react-icons/fa";
import { SiTailwindcss, SiSupabase } from "react-icons/si";
import i18n from "../i18n";

// Mini hook i18n safe (évite TS2589)
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null
      ? String((i18n.t as any)(key, options))
      : String((i18n.t as any)(key));
  return { t, lng };
}

export const Footer = () => {
  const { t } = useI18nSafe();
  const year = new Date().getFullYear();

const APP_VERSION = String(process.env.REACT_APP_VERSION ?? "1.4.0.2a");

  return (
    <footer
      className="w-full mt-10 border-t rounded-2xl
                 bg-white text-gray-700 border-gray-200
                 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-800
                 pb-[env(safe-area-inset-bottom)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col items-center gap-4 sm:gap-6">
        {/* Icônes sociales / tech */}
        <nav aria-label={t("footer:social.aria_nav")} className="w-full">
          <ul className="flex flex-wrap justify-center gap-4 sm:gap-6 text-gray-400 dark:text-gray-400">
            <li>
              <a
                href="https://www.youtube.com/@SwishData-k3l"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer:social.youtube_label")}
                className="p-2 rounded-xl hover:text-orange-500 dark:hover:text-orange-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 transition"
              >
                {BiLogoYoutube({ size: 26, className: "sm:size-7" }) as ReactElement}
              </a>
            </li>
            <li>
              <a
                href="https://fr.react.dev/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer:social.react_label")}
                className="p-2 rounded-xl hover:text-orange-500 dark:hover:text-orange-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 transition"
              >
                {FaReact({ size: 24, className: "sm:size-7" }) as ReactElement}
              </a>
            </li>
            <li>
              <a
                href="https://tailwindcss.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer:social.tailwind_label")}
                className="p-2 rounded-xl hover:text-orange-500 dark:hover:text-orange-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 transition"
              >
                {SiTailwindcss({ size: 24, className: "sm:size-7" }) as ReactElement}
              </a>
            </li>
            <li>
              <a
                href="https://supabase.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer:social.supabase_label")}
                className="p-2 rounded-xl hover:text-orange-500 dark:hover:text-orange-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 transition"
              >
                {SiSupabase({ size: 22, className: "sm:size-6" }) as ReactElement}
              </a>
            </li>
          </ul>
        </nav>

        {/* Liens texte */}
        <nav aria-label={t("footer:links.aria_nav")} className="w-full">
          <ul className="flex flex-col items-center gap-2 text-sm sm:text-base text-gray-500 dark:text-gray-400 sm:flex-row sm:justify-center sm:gap-4">
            <li>
              <Link
                to="/legal"
                className="px-2 py-1 rounded-md hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                aria-label={t("footer:links.legal")}
              >
                {t("footer:links.legal")}
              </Link>
            </li>
            <li className="hidden sm:block select-none">•</li>
            <li>
              <Link
                to="/rules"
                className="px-2 py-1 rounded-md hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                aria-label={t("footer:links.terms")}
              >
                {t("footer:links.terms")}
              </Link>
            </li>
            <li className="hidden sm:block select-none">•</li>
            <li>
              <a
                href="https://discord.gg/q5zR75uTB6"
                target="_blank"
                rel="noopener noreferrer"
                className="px-2 py-1 rounded-md hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                aria-label={t("footer:links.discord")}
              >
                {t("footer:links.discord")}
              </a>
            </li>
            <li className="hidden sm:block select-none">•</li>
            <li>
              <Link
                to="/contact"
                className="px-2 py-1 rounded-md hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                aria-label={t("footer:links.contact")}
              >
                {t("footer:links.contact")}
              </Link>
            </li>
          </ul>
        </nav>

        {/* Bas de page */}
        <div className="text-center space-y-1">
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {t("footer:bottom.copyright", {
              year,
              brand: "SwishData",
            })}
          </p>
          <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
            {t("footer:bottom.version", { v: APP_VERSION })}
          </p>
        </div>
      </div>
    </footer>
  );
};
